import { ReactElement } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  XAxisProps,
  YAxisProps,
} from "recharts";

export interface BarChartData {
  [key: string]: string | number;
}

export interface BarConfig {
  dataKey: string;
  fill: string;
  name: string;
}

export interface BarChartProps {
  data: BarChartData[];
  bars: BarConfig[];
  height?: number;
  xAxisKey?: string;
  xAxisProps?: Partial<XAxisProps>;
  yAxisProps?: Partial<YAxisProps>;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  gridProps?: {
    strokeDasharray?: string;
    horizontal?: boolean;
    vertical?: boolean;
  };
}

const BarChart = ({
  data,
  bars,
  height = 400,
  xAxisKey,
  xAxisProps = {},
  yAxisProps = {},
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  gridProps = {
    strokeDasharray: "3 3",
    horizontal: true,
    vertical: false,
  },
}: BarChartProps): ReactElement => {
  // Use the first bar's dataKey as xAxisKey if not provided
  const xAxisDataKey = xAxisKey || (bars.length > 0 ? Object.keys(data[0] || {})[0] : "");

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={data}>
        {showGrid && (
          <CartesianGrid
            strokeDasharray={gridProps.strokeDasharray}
            horizontal={gridProps.horizontal}
            vertical={gridProps.vertical}
          />
        )}
        <XAxis
          dataKey={xAxisDataKey}
          angle={-45}
          textAnchor="end"
          tick={{ fontSize: 12 }}
          height={40}
          {...xAxisProps}
        />
        <YAxis tick={{ fontSize: 12 }} width={20} {...yAxisProps} />
        {showTooltip && <Tooltip />}
        {showLegend && <Legend />}
        {bars.map((bar, index) => (
          <Bar key={bar.dataKey || index} dataKey={bar.dataKey} fill={bar.fill} name={bar.name} />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default BarChart;
